import { AdminOrganizations } from '@platform/client';
import { AdminOrgMatchRecord } from '@platform/client/schemas';
import { ColumnDef, createColumnHelper } from '@tanstack/react-table';
import { useContext, useMemo } from 'react';
import { formatDateStr } from '../../../dashboard/my-projects/utils';
import { ProjectLink } from '../projects/ProjectsAdmin';
import { ProfessionalLink } from '../professionals/ProfessionalsAdmin';
import { NoRecordsFoundTabContent, ProfileTabContainer } from '../../components/ProfileComponents';
import { AdminTable, AdminTableContext } from '../../AdminTable';
import { IntroductionLink } from '../introductions/IntroductionsAdmin';

const matchColumnHelper = createColumnHelper<AdminOrgMatchRecord>();

export const MatchingTabContent = ({ organizationId }: { organizationId: number }) => {
  const { adminQueryOptionsV2 } = useContext(AdminTableContext);
  const {
    data: matchData,
    isLoading,
    isError,
    isSuccess,
  } = AdminOrganizations.useGetOrganizationMatchData({
    organization_id: organizationId,
    page: adminQueryOptionsV2.page,
    limit: adminQueryOptionsV2.limit,
    sort: adminQueryOptionsV2.sort,
  });
  const columns = useMemo(
    () =>
      [
        matchColumnHelper.accessor('introductionId', {
          header: 'Intro Id',
          cell: (info) => (
            <IntroductionLink
              introductionId={info.getValue()}
              introductionName={info.getValue().toString()}
            />
          ),
          size: 5,
        }),
        matchColumnHelper.accessor('introductionInitiationType', {
          header: 'Intro Type',
          cell: (info) => (info.getValue() === 'Organization' ? 'Sent' : 'Received'),
          size: 10,
        }),
        matchColumnHelper.accessor('introductionCreatedAt', {
          header: 'Date',
          cell: (info) => formatDateStr(info.getValue()),
          size: 1,
        }),
        matchColumnHelper.accessor('projectName', {
          header: 'Project',
          cell: ({ row }) =>
            row.original.projectId ?
              <ProjectLink
                projectId={row.original.projectId}
                projectName={row.original.projectName}
              />
            : null,
          size: 300,
        }),
        matchColumnHelper.accessor('representativeName', {
          header: 'Representative',
          cell: (info) => info.getValue(),
        }),
        matchColumnHelper.accessor('professionalName', {
          header: 'Professional',
          cell: ({ row }) => (
            <ProfessionalLink
              userId={row.original.professionalId}
              name={row.original.professionalName}
            />
          ),
        }),
        matchColumnHelper.accessor('myProjectsStatus', {
          header: 'Status',
          cell: (info) => info.getValue(),
          size: 300,
        }),
        matchColumnHelper.accessor('myProjectsStatusDate', {
          header: 'Status date',
          cell: (info) => formatDateStr(info.getValue()),
          size: 150,
        }),
      ] as ColumnDef<AdminOrgMatchRecord>[],
    [],
  );

  return (
    <ProfileTabContainer>
      {isSuccess && matchData?.matchRecords.length === 0 ?
        <NoRecordsFoundTabContent recordType='organization' />
      : <AdminTable
          isError={isError}
          isLoading={isLoading}
          isSuccess={isSuccess}
          columns={columns}
          data={matchData?.matchRecords || []}
          isSimple
          filteredCount={matchData?.totalCount ?? 0}
        />
      }
    </ProfileTabContainer>
  );
};
